import React, { FC, KeyboardEvent, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { BiSearchAlt } from 'react-icons/bi';

import { useAppDispatch, useAppSelector } from '../../redux/redux.hooks';
import { getAllShops } from '../../reducers/shops.reducer';
import { Shop } from '../../models/shop';
import { openShop, shopMatches } from '../../utils/open.shop';
import { useFavouriteShops } from '../../hooks/use.favourite.shops';

const MAX_RESULTS = 8;

// Shop switcher for the existing-shop header. Follows Autocomplete's ARIA 1.2
// combobox pattern (native input, listbox in the top layer so the header can't clip
// it), but matches name, visible name or ID. Until you type it lists favourite shops
// only, since there are thousands of shops.
const HeaderShopSearch: FC = () => {
  const id = useId();
  const listboxId = `${id}-listbox`;
  const navigate = useNavigate();
  const { id: currentShopId } = useParams();
  const dispatch = useAppDispatch();
  const { data, status } = useAppSelector((state) => state.shopsReducer);
  const { favourites } = useFavouriteShops();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(null);

  // A no-op when another view already loaded the shops (see getAllShops).
  useEffect(() => {
    dispatch(getAllShops());
  }, [dispatch]);

  const needle = query.trim();
  const results = useMemo(
    () => (needle ? data.filter((shop) => shopMatches(shop, needle)) : []),
    [data, needle],
  );
  const favouriteShops = useMemo(
    () => favourites
      .map((shopId) => data.find((shop) => shop.shop_id === shopId))
      .filter((shop): shop is Shop => !!shop),
    [data, favourites],
  );
  const visible = needle ? results.slice(0, MAX_RESULTS) : favouriteShops;
  const optionId = (index: number) => `${id}-option-${index}`;

  let message: string | undefined;
  if (status === 'loading' || status === 'idle') message = 'Loading shops…';
  else if (status === 'error') message = 'Couldn’t load shops';
  else if (!visible.length) message = 'No shops found';
  // With an empty field the popover only opens to list favourites.
  const expanded = open && (!!needle || (favourites.length > 0 && (status !== 'successful' || favouriteShops.length > 0)));

  useLayoutEffect(() => {
    if (!expanded) return;
    const update = () => {
      const rect = inputRef.current?.getBoundingClientRect();
      if (rect) setPosition({ top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 320) });
    };
    update();
    // Native popovers only: the Polaris bundle polyfills showPopover in jsdom, where
    // the list would then stay hidden (see Autocomplete).
    const listbox = listboxRef.current;
    if (listbox && typeof CSS !== 'undefined' && CSS.supports?.('selector(:popover-open)')) {
      listbox.setAttribute('popover', 'manual');
      listbox.showPopover();
    }
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [expanded]);

  const close = () => {
    setOpen(false);
    setActive(-1);
  };

  const select = (shop: Shop) => {
    setQuery('');
    close();
    inputRef.current?.blur();
    openShop(shop, navigate, currentShopId);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!visible.length) return;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setOpen(true);
      setActive((current) => (current + step + visible.length) % visible.length);
    } else if (event.key === 'Enter' && open && visible.length) {
      event.preventDefault();
      select(visible[Math.max(active, 0)]);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      if (open && needle) close();
      else setQuery('');
    }
  };

  return (
    <Wrapper>
      <Label htmlFor={`${id}-input`}>Search shops</Label>
      <Icon aria-hidden='true' />
      <Input
        ref={inputRef}
        id={`${id}-input`}
        type='search'
        role='combobox'
        autoComplete='off'
        spellCheck={false}
        aria-autocomplete='list'
        aria-expanded={expanded && visible.length > 0}
        aria-controls={listboxId}
        aria-activedescendant={expanded && active >= 0 ? optionId(active) : undefined}
        placeholder='Search shops by name or ID'
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setActive(-1);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={close}
        onKeyDown={onKeyDown}
      />
      {expanded && <Popover ref={listboxRef} style={position ?? undefined}>
        {!needle && <PopoverHeading>Favourites</PopoverHeading>}
        {message ? <Message role='status'>{message}</Message> : <>
          <Listbox id={listboxId} role='listbox' aria-label={needle ? 'Shops' : 'Favourite shops'}>
            {visible.map((shop, index) => (
              <Option
                // shop_id isn't unique (several test shops share one), so qualify the key.
                key={`${shop.shop_id}-${index}`}
                id={optionId(index)}
                role='option'
                aria-selected={index === active}
                $active={index === active}
                // Keeps focus in the input, so blur doesn't close the list before the click.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => select(shop)}
              >
                <OptionName>{shop.visible_name && `[${shop.visible_name}] `}{shop.shop_name}</OptionName>
                <OptionId>{shop.shop_id}</OptionId>
              </Option>
            ))}
          </Listbox>
          {results.length > MAX_RESULTS && (
            <Message>{results.length - MAX_RESULTS} more – keep typing to narrow down</Message>
          )}
        </>}
      </Popover>}
    </Wrapper>
  );
};

export default HeaderShopSearch;

const Wrapper = styled.div`
  position: relative;
  flex: 1 1 280px;
  max-width: 420px;
  min-width: 200px;
`;

const Label = styled.label`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const Icon = styled(BiSearchAlt)`
  position: absolute;
  top: 50%;
  left: 10px;
  transform: translateY(-50%);
  color: #616161;
  pointer-events: none;
`;

const Input = styled.input`
  box-sizing: border-box;
  width: 100%;
  min-height: 32px;
  padding: 6px 12px 6px 32px;
  border: 1px solid #c9cccf;
  border-radius: 8px;
  background: #f6f6f7;
  color: #303030;
  font: inherit;
  font-size: 13px;

  &:hover {
    border-color: #8a8a8a;
  }

  &:focus {
    background: #fff;
  }

  &:focus-visible {
    outline: 2px solid #005bd3;
    outline-offset: 1px;
  }
`;

const Popover = styled.div`
  position: fixed;
  inset: auto;
  z-index: 10;
  box-sizing: border-box;
  max-height: 360px;
  overflow-y: auto;
  margin: 0;
  padding: 6px;
  background: #fff;
  border-radius: 12px;
  border: 0;
  color: inherit;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.06);
`;

const Listbox = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Option = styled.li<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 8px;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  background: ${(props) => (props.$active ? '#f1f1f1' : 'transparent')};

  &:hover {
    background: #f1f1f1;
  }
`;

const OptionName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 550;
`;

const OptionId = styled.span`
  flex-shrink: 0;
  padding: 0 6px;
  border-radius: 6px;
  background: #ebebeb;
  color: #4a4a4a;
  font-size: 12px;
`;

const PopoverHeading = styled.div`
  padding: 6px 8px 4px;
  font-size: 12px;
  font-weight: 600;
  color: #616161;
`;

const Message = styled.div`
  padding: 6px 8px;
  font-size: 13px;
  color: #616161;
`;
